import React, { FC, useContext } from "react";
import Link from "../../Link";
import { MyPriceListStateContext } from "../../../context/myPriceListContext";
import Menu from "../../../../assets/icons/hamburger-menu.svg";
import MobileMenu from "./MobileMenu";
import {
  GRAIN_CONVEYORS,
  BUCKET_ELEVATORS,
  INCLINE_CONVEYORS,
} from "../../../const";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { normalizePath } from "@/utility";

const navigationMenu = [
  {
    caption: GRAIN_CONVEYORS,
    link: "/en-masse-conveyors",
  },
  {
    caption: BUCKET_ELEVATORS,
    link: "/bucket-elevators",
  },
  {
    caption: INCLINE_CONVEYORS,
    link: "/incline-conveyors",
  },
  {
    caption: "Contact",
    link: "/contact",
  },
  {
    caption: "About us",
    link: "/about",
  },
  {
    caption: "All products",
    link: "/all-products",
  },
  {
    caption: "My products",
    link: "/my-products",
  },
];

interface MainNavigationBaseProps {
  location: Location;
  variant: "menu" | "list";
}

interface MainNavigationMobileProps extends MainNavigationBaseProps {
  variant: "menu";
  mobileMenuOpen: boolean;
  onMobileMenuOpenChange: (open: boolean) => void;
  onMobileMenuClose: () => void;
}

interface MainNavigationListProps extends MainNavigationBaseProps {
  variant: "list";
  mobileMenuOpen?: never;
  onMobileMenuOpenChange?: never;
  onMobileMenuClose?: never;
}

type MainNavigationProps =
  | MainNavigationMobileProps
  | MainNavigationListProps;

const MainNavigation: FC<MainNavigationProps> = ({
  location,
  variant,
  mobileMenuOpen,
  onMobileMenuOpenChange,
  onMobileMenuClose,
}) => {
  const myPriceList = useContext(MyPriceListStateContext);

  if (variant === "menu") {
    return (
      <Popover
        open={mobileMenuOpen}
        onOpenChange={onMobileMenuOpenChange}
        modal
      >
        <PopoverTrigger className="relative">
          <Button size={'iconLg'} className="rounded-full">
            <Menu alt="menu button" className="w-full" width={42} height={42} />
          </Button>
          {!!myPriceList.length && <div className="absolute w-2 h-2 bg-textAccent top-1 right-1 rounded-full" />}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 p-2 bg-yellowPrimary">
          <MobileMenu
            onClose={onMobileMenuClose}
            open={mobileMenuOpen}
            navigationMenu={navigationMenu}
            path={location.pathname}
            myPriceList={myPriceList}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <ul className="m-0 p-0 mb-4 grid grid-cols-7 gap-1 list-none">
      {navigationMenu.map((menuItem) => {
        const showBadge = menuItem.link === "/my-products";
        return (
          <li key={menuItem.link} className="relative">
            <Link
              to={menuItem.link}
              className={cn(
                'w-full h-full block box-border text-white no-underline uppercase font-krub font-bold text-sm leading-[18px] text-center border border-transparent transition-all duration-300 px-1 py-0.5 hover:border-yellowPrimary',
                {
                  'bg-[#E7B607A6]':
                    normalizePath(location.pathname).includes(normalizePath(menuItem.link)),
                }
              )}
            >
              {menuItem.caption}
            </Link>
            {showBadge && (
              <Badge className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-textAccent pointer-events-none">
                {String(myPriceList.length)}
              </Badge>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default MainNavigation;
